import React from 'react';
import {
  Container,
  Flex,
  Heading,
  Section,
  Stack,
} from '@grubhubprod/gh-cookbook-react';
import { useParams } from 'react-router-dom';
import { useGetParentCorpData } from 'src/components/enterprise/Parents/Children/ParentChildAccountController/hooks/useGetParentCorpData';
import PageHeader from 'src/components/enterprise/layout/PageHeader';
import PageSection from 'src/components/enterprise/layout/PageSection';
import { useChildCorpSearchWithParams } from 'src/components/enterprise/Parents/Admin/hooks/useChildCorpSearchWithParams/useChildCorpSearchWithParams';
import { ChildAccountRow } from 'src/components/enterprise/Parents/Admin/ChildAccountsController/ChildAccountRow/ChildAccountRow';
import { TieredAccountsTable } from 'src/components/enterprise/Parents/Admin/TieredAccountTable/TieredAccountsTable';
import { Breadcrumbs } from 'src/components/Breadcrumbs/Breadcrumbs';
import { useGetParentAccountsRedirectUrl } from 'src/components/enterprise/Parents/Admin/hooks/useGetParentAccountsRedirectUrl/useGetParentAccountsRedirectUrl';
import { TIERED_ACCOUNTS_PAGE_SIZE } from 'src/components/enterprise/Parents/constants';
import { Pagination } from 'src/components/Pagination/Pagination';
import { ISearchParams, SearchStatus } from 'src/utilities/useSearchHooks';
import { NumberOfChildAccounts } from './NumberOfChildAccounts/NumberOfChildAccounts';

export const ChildAccountsController: React.FC = () => {
  const { corpid: corpId } = useParams<{ corpid: string }>();

  const { isLoading, data } = useGetParentCorpData(corpId);

  const { searchResults } = useChildCorpSearchWithParams(
    corpId,
    TIERED_ACCOUNTS_PAGE_SIZE
  );

  const {
    resultsList,
    totalPages,
    currentSearchPage,
    totalResults,
    firstItemNumberFromPage,
    lastItemNumberFromPage,
    searchStatus,
    doSearch,
  } = searchResults;

  const isChildCorpDataLoading = searchStatus === SearchStatus.LOADING;

  if (isLoading) {
    return null;
  }

  const handlePageChange = (page = 1) => {
    const params: ISearchParams = { page };
    doSearch(params);
  };

  const parentAccountName = data?.name || '';
  const noResultsMsg = 'No child accounts';
  const headers = ['Linked account', 'Firm designator'];

  const parentAccountsUrl = useGetParentAccountsRedirectUrl();

  return (
    <Container variant="default">
      <Section inset="sm">
        <PageHeader>
          <Stack y="sm">
            <Heading variant="h2">{parentAccountName}</Heading>
          </Stack>
          <Breadcrumbs breadcrumbs={[parentAccountsUrl]} />
        </PageHeader>

        <Stack y="lg">
          <PageSection>
            <Stack y="sm">
              <Heading variant="h3">Child accounts</Heading>
            </Stack>
            <Stack y="lg">
              <NumberOfChildAccounts
                firstItemNumberFromPage={firstItemNumberFromPage}
                lastItemNumberFromPage={lastItemNumberFromPage}
                totalResults={totalResults}
              />
            </Stack>
            <Stack y="md">
              <TieredAccountsTable
                numberOfAccounts={resultsList.length}
                noResultsMsg={noResultsMsg}
                headers={headers}
                scrollable={false}
                isLoading={isChildCorpDataLoading}
              >
                {resultsList.map(({ name, firm_designator, id }) => (
                  <ChildAccountRow
                    key={id}
                    name={name}
                    firmDesignator={firm_designator}
                    corpId={id}
                  />
                ))}
              </TieredAccountsTable>
            </Stack>
            {totalPages > 1 && (
              <Flex justifyContent="center">
                <Pagination
                  page={currentSearchPage}
                  pageSize={TIERED_ACCOUNTS_PAGE_SIZE}
                  ellipses={false}
                  rotate
                  collectionSize={totalResults}
                  pageChange={handlePageChange}
                />
              </Flex>
            )}
          </PageSection>
        </Stack>
      </Section>
    </Container>
  );
};
